import React, { useEffect, useMemo, useState } from "react";
import styles from "./CustomCalendar.module.css";

const STATUS_CLASS = {
  present: "statusPresent",
  absent:  "statusAbsent",
  leave:   "statusLeave",
  holiday: "statusHoliday",
};

const STATUS_LABEL = {
  present: "Present",
  absent:  "Absent",
  leave:   "Leave",
  holiday: "Holiday",
};

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

/* ---------- Date helpers ---------- */
function parseMonth(yyyymm) {
  if (!yyyymm || typeof yyyymm !== "string") {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  }
  const [y, m] = yyyymm.split("-").map(Number);
  return { year: y, month: m };
}

function formatMonthLabel(yyyymm) {
  const { year, month } = parseMonth(yyyymm);
  const date = new Date(year, month - 1);
  return date.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function getFirstWeekday(year, month) {
  return new Date(year, month - 1, 1).getDay();
}

function shiftMonth(yyyymm, delta) {
  const { year, month } = parseMonth(yyyymm);
  const date = new Date(year, month - 1 + delta, 1);
  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, "0");
  return `${newYear}-${newMonth}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function currentYyyymm() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

export default function CustomCalendar({
  data,
  month,                       // ✅ optional controlled month from parent
  onMonthChange = () => {},
  onMonthPicker = () => {},
  title = "Attendance Calendar",
}) {
  /* ✅ Internal source of truth for navigation.
   * Initialized from controlled `month` prop, then from `data.month`,
   * else falls back to current month. */
  const [viewMonth, setViewMonth] = useState(
    month || data?.month || currentYyyymm()
  );

  /* ✅ Sync internal state when parent's controlled `month` prop changes */
  useEffect(() => {
    if (month && month !== viewMonth) {
      setViewMonth(month);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  /* ✅ Also sync if `data.month` arrives later and differs (initial load) */
  useEffect(() => {
    if (!month && data?.month && data.month !== viewMonth) {
      setViewMonth(data.month);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.month]);

  /* Map date → status for O(1) lookup */
  const statusByDate = useMemo(() => {
    const map = {};
    (data?.days || []).forEach((d) => {
      if (d?.date) map[d.date] = d.status;
    });
    return map;
  }, [data]);

  /* Build the calendar cells from viewMonth, not data.month */
  const cells = useMemo(() => {
    const { year, month: mm } = parseMonth(viewMonth);
    const daysInMonth = getDaysInMonth(year, mm);
    const firstWeekday = getFirstWeekday(year, mm);
    const out = [];

    for (let i = 0; i < firstWeekday; i++) {
      out.push({ empty: true, key: `empty-${i}` });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${pad2(mm)}-${pad2(day)}`;
      out.push({
        empty: false,
        day,
        dateStr,
        status: statusByDate[dateStr] || null,
        key: dateStr,
      });
    }
    return out;
  }, [viewMonth, statusByDate]);

  const monthLabel = formatMonthLabel(viewMonth);
  const monthLong = monthLabel.split(" ")[0];

  /* ✅ Update internal state immediately AND notify parent */
  const handlePrev = () => {
    const prev = shiftMonth(viewMonth, -1);
    setViewMonth(prev);
    onMonthChange(prev);
  };

  const handleNext = () => {
    const next = shiftMonth(viewMonth, 1);
    setViewMonth(next);
    onMonthChange(next);
  };

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <svg
          className={styles.titleIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <line x1="8" y1="3" x2="8" y2="7" />
          <line x1="16" y1="3" x2="16" y2="7" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <h2 className={styles.title}>{title}</h2>
      </div>

      {/* Month navigation */}
      <div className={styles.monthNav}>
        <button
          type="button"
          className={styles.monthLabel}
          onClick={onMonthPicker}
          aria-label={`Open month picker (currently ${monthLabel})`}
        >
          <span>{monthLabel}</span>
          <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor" aria-hidden="true">
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </button>

        <div className={styles.navButtons}>
          <button
            type="button"
            className={styles.navButton}
            onClick={handlePrev}
            aria-label="Previous month"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className={styles.navButton}
            onClick={handleNext}
            aria-label="Next month"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className={styles.weekdays}>
        {WEEKDAYS.map((w, i) => (
          <div key={`wd-${i}`} className={styles.weekday}>
            {w}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className={styles.grid}>
        {cells.map((cell) => {
          if (cell.empty) {
            return <div key={cell.key} className={styles.empty} aria-hidden="true" />;
          }

          const statusClass = cell.status ? styles[STATUS_CLASS[cell.status]] : "";
          const ariaLabel = cell.status
            ? `${monthLong} ${cell.day}, ${STATUS_LABEL[cell.status]}`
            : `${monthLong} ${cell.day}`;

          return (
            <div
              key={cell.key}
              className={`${styles.dayCircle} ${statusClass}`}
              role="img"
              aria-label={ariaLabel}
            >
              {cell.day}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className={styles.legend} role="list">
        {Object.keys(STATUS_LABEL).map((status) => (
          <span key={status} className={styles.legendItem} role="listitem">
            <span
              className={`${styles.legendDot} ${styles[STATUS_CLASS[status]]}`}
              aria-hidden="true"
            />
            {STATUS_LABEL[status]}
          </span>
        ))}
      </div>
    </div>
  );
}